import { getDefaultDkSisSearchConditionValue } from '../utils/graphConditionSelectorUtils';
import {
  DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION,
  DATA_KI_GRAPH_MAX_SLOTS,
  DataKiGraphFieldItem,
  DataKiGraphParams,
  DataKiGraphSlotConfig,
  GRAPH_FIELD_TYPE,
  GraphFieldType,
} from './dataKiGraph';
import { DkSisSearchParams } from './dkSis';

export const graphSlotKey = (slot: DataKiGraphSlotConfig): string =>
  `${slot.field}:${slot.dkSisCondition}`;

export const normalizeGraphSlotsForFetch = (
  slots: readonly DataKiGraphSlotConfig[],
  maxSlots: number = DATA_KI_GRAPH_MAX_SLOTS
): DataKiGraphSlotConfig[] =>
  slots.filter((slot) => slot.field !== '').slice(0, maxSlots);

type LegacyDkSisParams = Pick<
  DataKiGraphParams,
  | 'needsSis'
  | 'sisBreakEvenForPachinko'
  | 'sisBreakEvenForPachislo'
  | 'sisRank'
  | 'sisArea'
>;

export const isLegacyDkSisEnabled = (params: LegacyDkSisParams): boolean =>
  params.needsSis === true ||
  params.sisBreakEvenForPachinko !== undefined ||
  params.sisBreakEvenForPachislo !== undefined ||
  params.sisRank !== undefined ||
  params.sisArea !== undefined;

const resolveDkSisConditionFromParams = (params: LegacyDkSisParams): string =>
  getDefaultDkSisSearchConditionValue(isLegacyDkSisEnabled(params), params);

export const resolveDataKiGraphSlots = (
  params: DataKiGraphParams
): DataKiGraphSlotConfig[] => {
  if (params.graphSlots && params.graphSlots.length > 0) {
    return params.graphSlots.slice(0, DATA_KI_GRAPH_MAX_SLOTS);
  }
  if (params.field) {
    return [
      {
        field: params.field,
        dkSisCondition: resolveDkSisConditionFromParams(params),
      },
    ];
  }
  return [
    {
      field: GRAPH_FIELD_TYPE.OUT,
      dkSisCondition: DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION,
    },
  ];
};

export const resolveDataKiGraphSlotsForSearch = (
  existingGraphParams: DataKiGraphParams | undefined,
  initialDkSisCondition: string = DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION
): DataKiGraphSlotConfig[] => {
  if (existingGraphParams) {
    return resolveDataKiGraphSlots(existingGraphParams);
  }
  return [
    {
      field: GRAPH_FIELD_TYPE.OUT,
      dkSisCondition: initialDkSisCondition,
    },
  ];
};

export const resolveDkSisConditionFromSearchParams = (
  params: Pick<
    DkSisSearchParams,
    | 'needsSis'
    | 'sisBreakEvenForPachinko'
    | 'sisBreakEvenForPachislo'
    | 'sisRank'
    | 'sisArea'
  >
): string => resolveDkSisConditionFromParams(params);

export const shouldRefetchDataKiGraphSlots = (
  prevSlots: readonly DataKiGraphSlotConfig[],
  nextSlots: readonly DataKiGraphSlotConfig[]
): boolean =>
  normalizeGraphSlotsForFetch(prevSlots).map(graphSlotKey).join('|') !==
  normalizeGraphSlotsForFetch(nextSlots).map(graphSlotKey).join('|');

/** 表示項目は同じで DK-SIS 条件だけ変わったか */
export const isDkSisConditionChangeOnly = (
  prevSlots: readonly DataKiGraphSlotConfig[],
  nextSlots: readonly DataKiGraphSlotConfig[]
): boolean => {
  const prevNorm = normalizeGraphSlotsForFetch(prevSlots);
  const nextNorm = normalizeGraphSlotsForFetch(nextSlots);
  if (prevNorm.length !== nextNorm.length) {
    return false;
  }
  const hasDkSisChange = prevNorm.some(
    (prev, index) => prev.dkSisCondition !== nextNorm.at(index)?.dkSisCondition
  );
  if (!hasDkSisChange) {
    return false;
  }
  return prevNorm.every(
    (prev, index) => prev.field === nextNorm.at(index)?.field
  );
};

/** 再取得が必要なスロットだけ返す */
export const resolveChangedGraphSlotsForRefetch = (
  prevSlots: readonly DataKiGraphSlotConfig[],
  nextSlots: readonly DataKiGraphSlotConfig[]
): DataKiGraphSlotConfig[] => {
  const prevNorm = normalizeGraphSlotsForFetch(prevSlots);
  const nextNorm = normalizeGraphSlotsForFetch(nextSlots);
  return nextNorm.filter((slot, index) => {
    const prev = prevNorm.at(index);
    return !prev || graphSlotKey(prev) !== graphSlotKey(slot);
  });
};

const areRemainingGraphSlotsInOrder = (
  remainingSlots: readonly DataKiGraphSlotConfig[],
  originalSlots: readonly DataKiGraphSlotConfig[]
): boolean =>
  remainingSlots.reduce<{ searchIndex: number; isMatch: boolean }>(
    (state, slot) => {
      if (!state.isMatch) {
        return state;
      }
      const relativeIndex = originalSlots
        .slice(state.searchIndex)
        .findIndex(
          (candidate) => graphSlotKey(candidate) === graphSlotKey(slot)
        );
      if (relativeIndex === -1) {
        return { searchIndex: state.searchIndex, isMatch: false };
      }
      return {
        searchIndex: state.searchIndex + relativeIndex + 1,
        isMatch: true,
      };
    },
    { searchIndex: 0, isMatch: true }
  ).isMatch;

export const isGraphSlotsRemovalOnly = (
  prevSlots: readonly DataKiGraphSlotConfig[],
  nextSlots: readonly DataKiGraphSlotConfig[]
): boolean => {
  const normalizedPrev = normalizeGraphSlotsForFetch(prevSlots);
  const normalizedNext = normalizeGraphSlotsForFetch(nextSlots);
  if (normalizedNext.length >= normalizedPrev.length) {
    return false;
  }
  return areRemainingGraphSlotsInOrder(normalizedNext, normalizedPrev);
};

export const buildGraphSlotsAfterAddEmptySlot = (
  currentSlots: readonly DataKiGraphSlotConfig[],
  maxSlots: number = DATA_KI_GRAPH_MAX_SLOTS
): DataKiGraphSlotConfig[] => {
  if (currentSlots.length >= maxSlots) {
    return currentSlots.slice(0, maxSlots);
  }
  return [
    ...currentSlots,
    {
      field: '' as const,
      dkSisCondition: DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION,
    },
  ].slice(0, maxSlots);
};

export const buildGraphSlotsAfterRemove = (
  currentSlots: readonly DataKiGraphSlotConfig[],
  index: number,
  maxSlots: number = DATA_KI_GRAPH_MAX_SLOTS
): DataKiGraphSlotConfig[] => {
  if (index < 0 || index >= currentSlots.length || currentSlots.length <= 1) {
    return currentSlots.slice(0, maxSlots);
  }
  return currentSlots
    .filter((_, slotIndex) => slotIndex !== index)
    .slice(0, maxSlots);
};

const resolveUniqueDkSisConditionFromOtherSlots = (
  currentSlots: readonly DataKiGraphSlotConfig[],
  targetIndex: number,
  isFieldSupportsSis: (field: GraphFieldType) => boolean
): string | undefined => {
  const uniqueConditions = new Set(
    currentSlots
      .filter(
        (slot, slotIndex) =>
          slotIndex !== targetIndex &&
          slot.field !== '' &&
          isFieldSupportsSis(slot.field) &&
          slot.dkSisCondition !== DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION
      )
      .map((slot) => slot.dkSisCondition)
  );
  return uniqueConditions.size === 1 ? [...uniqueConditions].at(0) : undefined;
};

export const buildGraphSlotsAfterFieldChange = (
  currentSlots: readonly DataKiGraphSlotConfig[],
  index: number,
  field: GraphFieldType | '',
  isFieldSupportsSis: (field: GraphFieldType) => boolean,
  maxSlots: number = DATA_KI_GRAPH_MAX_SLOTS
): DataKiGraphSlotConfig[] => {
  if (index < 0 || index >= maxSlots) {
    return currentSlots.slice(0, maxSlots);
  }

  const slotCount = Math.min(
    Math.max(currentSlots.length, index + 1),
    maxSlots
  );

  return Array.from({ length: slotCount }, (_, slotIndex) => {
    const currentSlot =
      currentSlots.at(slotIndex) ??
      ({
        field: '',
        dkSisCondition: DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION,
      } as DataKiGraphSlotConfig);

    if (slotIndex !== index) {
      return currentSlot;
    }
    if (field === '') {
      return {
        field,
        dkSisCondition: DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION,
      };
    }

    const isInitialFieldSelection = currentSlot.field === '';
    const supportsSis = isFieldSupportsSis(field);
    const inheritedCondition = isInitialFieldSelection
      ? resolveUniqueDkSisConditionFromOtherSlots(
          currentSlots,
          index,
          isFieldSupportsSis
        )
      : undefined;

    // 空スロットへの初回項目選択時は、他スロットの SIS 条件が1種に定まるときだけ引き継ぐ。
    // 複数種ある場合や実条件が0件の場合は noGraph のまま。
    // 同一スロット内の項目変更時は当該スロットの条件を維持する。
    // 非対応項目への変更時は noGraph に落とす。
    const dkSisCondition = supportsSis
      ? inheritedCondition ?? currentSlot.dkSisCondition
      : DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION;

    return {
      field,
      dkSisCondition,
    };
  });
};

export const buildGraphSlotsAfterDkSisChange = (
  currentSlots: readonly DataKiGraphSlotConfig[],
  index: number,
  dkSisCondition: string,
  maxSlots: number = DATA_KI_GRAPH_MAX_SLOTS
): DataKiGraphSlotConfig[] => {
  if (index < 0 || index >= maxSlots) {
    return currentSlots.slice(0, maxSlots);
  }

  const slotCount = Math.min(
    Math.max(currentSlots.length, index + 1),
    maxSlots
  );

  return Array.from({ length: slotCount }, (_, slotIndex) => {
    const currentSlot =
      currentSlots.at(slotIndex) ??
      ({
        field: '',
        dkSisCondition: DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION,
      } as DataKiGraphSlotConfig);
    return slotIndex === index
      ? { ...currentSlot, dkSisCondition }
      : currentSlot;
  });
};

export const resolveGraphListIndexForSlot = (
  slots: readonly DataKiGraphSlotConfig[],
  slotIndex: number
): number =>
  slots.slice(0, slotIndex).filter((slot) => slot.field !== '').length;

export const resolveGraphItemForSlot = (
  graphList: readonly DataKiGraphFieldItem[],
  slots: readonly DataKiGraphSlotConfig[],
  slotIndex: number,
  options: { keepPreviousByIndex?: boolean } = {}
): DataKiGraphFieldItem | undefined => {
  const slot = slots.at(slotIndex);
  if (slot === undefined || slot.field === '') {
    return undefined;
  }
  const graphListIndex = resolveGraphListIndexForSlot(slots, slotIndex);
  const itemByIndex = graphList.at(graphListIndex);
  // 完全一致（表示項目 + DK-SIS 条件）のデータがあれば常にそれを表示する
  if (
    itemByIndex?.field === slot.field &&
    itemByIndex.dkSisCondition === slot.dkSisCondition
  ) {
    return itemByIndex;
  }
  const exactMatch = graphList.find(
    (item) =>
      item.field === slot.field && item.dkSisCondition === slot.dkSisCondition
  );
  if (exactMatch) {
    return exactMatch;
  }
  // 完全一致が無い場合、取得「中」だけ暫定表示でチラつきを抑える
  // （DK-SIS 条件変更中は同じ表示項目の旧データ、項目変更中は位置優先の旧データ）。
  // 取得完了後（失敗含む）は条件・項目が食い違うデータを見せず、
  // undefined を返して空の軸フレーム表示に委ねる。
  if (options.keepPreviousByIndex) {
    if (itemByIndex !== undefined) {
      return itemByIndex;
    }
    return graphList.find((item) => item.field === slot.field);
  }
  return undefined;
};

/** スロット設定に一致する取得済みデータが graphList に存在するか */
export const isSlotDataLoaded = (
  graphList: readonly DataKiGraphFieldItem[],
  slot: DataKiGraphSlotConfig
): boolean =>
  slot.field !== '' &&
  graphList.some(
    (item) =>
      item.field === slot.field && item.dkSisCondition === slot.dkSisCondition
  );

/**
 * 各スロットに「再取得待ち（ローディング表示すべき）」フラグを立てる。
 * 一部スロットだけ再取得中（DK-SIS 条件変更や項目変更）の場合は、その未着スロットだけを対象にし、
 * どのスロットも取得済みデータと一致する（期間変更・初回取得など全スロット再取得）場合は、
 * 全スロットをローディング対象にする。
 */
export const resolvePendingSlotFlags = (
  slots: readonly DataKiGraphSlotConfig[],
  graphList: readonly DataKiGraphFieldItem[],
  isLoading: boolean
): boolean[] => {
  if (!isLoading) {
    return slots.map(() => false);
  }
  const missingDataFlags = slots.map(
    (slot) => slot.field !== '' && !isSlotDataLoaded(graphList, slot)
  );
  const hasTargetedSlot = missingDataFlags.some(Boolean);
  return slots.map((slot, index) =>
    slot.field === ''
      ? false
      : hasTargetedSlot
      ? missingDataFlags.at(index) ?? false
      : true
  );
};

export const buildGraphListForNormalizedSlots = (
  currentGraphList: readonly DataKiGraphFieldItem[],
  normalizedSlots: readonly DataKiGraphSlotConfig[]
): DataKiGraphFieldItem[] => {
  const graphByKey = new Map(
    currentGraphList.map((item) => [
      graphSlotKey({
        field: item.field,
        dkSisCondition: item.dkSisCondition,
      }),
      item,
    ])
  );
  return normalizedSlots.flatMap((slot) => {
    const item = graphByKey.get(graphSlotKey(slot));
    return item ? [item] : [];
  });
};

export const mergeGraphSlotsFromGraphList = (
  prevSlots: readonly DataKiGraphSlotConfig[],
  graphList: readonly Pick<DataKiGraphFieldItem, 'field' | 'dkSisCondition'>[],
  maxSlots: number = DATA_KI_GRAPH_MAX_SLOTS
): DataKiGraphSlotConfig[] => {
  if (graphList.length === 0) {
    return [];
  }

  const slotsFromGraph = graphList.map((item) => ({
    field: item.field,
    dkSisCondition: item.dkSisCondition,
  }));
  const normalizedPrev = normalizeGraphSlotsForFetch(prevSlots, maxSlots);

  if (
    normalizedPrev.map(graphSlotKey).join('|') ===
    slotsFromGraph.map(graphSlotKey).join('|')
  ) {
    return prevSlots.slice(0, maxSlots);
  }

  if (
    normalizedPrev.length > slotsFromGraph.length &&
    slotsFromGraph.every((slot, index) => {
      const prev = normalizedPrev.at(index);
      return prev !== undefined && graphSlotKey(slot) === graphSlotKey(prev);
    })
  ) {
    return prevSlots.slice(0, maxSlots);
  }

  const isDkSisOnlyPendingSync =
    normalizedPrev.length === slotsFromGraph.length &&
    normalizedPrev.every((slot, index) => {
      const graphSlot = slotsFromGraph.at(index);
      return (
        graphSlot !== undefined &&
        slot.field === graphSlot.field &&
        graphSlotKey(slot) !== graphSlotKey(graphSlot)
      );
    });
  if (isDkSisOnlyPendingSync) {
    return prevSlots.slice(0, maxSlots);
  }

  const trailingEmptySlots = prevSlots
    .slice(slotsFromGraph.length)
    .filter((slot) => slot.field === '')
    .map(() => ({
      field: '' as const,
      dkSisCondition: DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION,
    }));

  return [...slotsFromGraph, ...trailingEmptySlots].slice(0, maxSlots);
};

export { syncGraphSlotKeys } from './graphSlotKeys';

/**
 * prevSlots[i] ↔ prevKeys[i] の対応を、スロットの内容（graphSlotKey）一致で nextSlots へ引き継ぐ。
 * 位置・長さではなく識別子で対応付けるため、中間スロットの削除（並列取得の一部失敗など）や
 * 並び替えが起きてもキーが食い違わない。マッチしない新規スロットだけ createKey() を割り当てる。
 * 同一内容が複数ある場合は先着順に消費する。
 */
export const alignGraphSlotKeys = (
  prevSlots: readonly DataKiGraphSlotConfig[],
  prevKeys: readonly string[],
  nextSlots: readonly DataKiGraphSlotConfig[],
  createKey: () => string
): string[] => {
  const available = prevSlots.map((slot, index) => ({
    identity: graphSlotKey(slot),
    key: prevKeys.at(index),
    used: false,
  }));
  return nextSlots.map((slot) => {
    const wantIdentity = graphSlotKey(slot);
    const match = available.find(
      (entry) =>
        !entry.used &&
        entry.key !== undefined &&
        entry.identity === wantIdentity
    );
    if (match?.key !== undefined) {
      match.used = true;
      return match.key;
    }
    return createKey();
  });
};
